import PropTypes from 'prop-types';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';
// @mui
import {
  Avatar,
  Badge,
  Box,
  Divider,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
// hooks
import useAuth from '../../../hooks/useAuth';
// utils
import {fToNow} from '../../../utils/formatTime';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../../utils/notifications';
import {subscribeWorkspaceEvents} from '../../../utils/workspaceRealtime';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import {IconButtonAnimate} from '../../../components/animate';

const ICONS = {
  reply: 'eva:message-square-fill',
  enrichment: 'mdi:database-check-outline',
  warmup_halfway: 'mdi:fire',
  warmup_complete: 'eva:checkmark-circle-2-fill',
  deal_won: 'eva:trending-up-fill',
  campaign_completed: 'eva:paper-plane-fill',
  referral: 'eva:gift-fill',
};

export default function NotificationsPopover() {
  const {user, workspace, refreshAccount} = useAuth();
  const workspaceId = workspace?.id;
  const userId = user?.id;
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(null);
  const loadSequence = useRef(0);
  const snapshotLoading = useRef(false);
  const pendingCreated = useRef(new Map());
  const pendingReads = useRef(new Map());

  const load = useCallback(() => {
    const sequence = ++loadSequence.current;
    if (!workspaceId) {
      snapshotLoading.current = false;
      setNotifications([]);
      return Promise.resolve();
    }
    snapshotLoading.current = true;
    return fetchNotifications(workspaceId)
      .then((items) => {
        if (sequence !== loadSequence.current) return;
        const merged = new Map(items.map((item) => [item.id, item]));
        pendingCreated.current.forEach((item, id) => merged.set(id, item));
        pendingReads.current.forEach((readAt, id) => {
          const item = merged.get(id);
          if (item) merged.set(id, {...item, read_at: readAt});
        });
        setNotifications([...merged.values()]
          .sort((left, right) => String(right.created_at || '').localeCompare(String(left.created_at || '')))
          .slice(0, 50));
        pendingCreated.current.clear();
        pendingReads.current.clear();
      })
      .catch(() => {
        if (sequence === loadSequence.current) {
          pendingCreated.current.clear();
          pendingReads.current.clear();
        }
      })
      .finally(() => {
        if (sequence === loadSequence.current) snapshotLoading.current = false;
      });
  }, [workspaceId]);

  const handleRealtimeEvent = useCallback((event) => {
    if (event?.workspaceId !== workspaceId) return;
    if (event.type === 'notification.created') {
      const notification = event.payload?.notification;
      if (!notification?.id) return;
      if (notification.type === 'enrichment' && typeof refreshAccount === 'function') {
        Promise.resolve(refreshAccount()).catch(() => {});
      }
      if (snapshotLoading.current) pendingCreated.current.set(notification.id, notification);
      setNotifications((items) => [notification, ...items.filter(({id}) => id !== notification.id)]
        .sort((left, right) => String(right.created_at || '').localeCompare(String(left.created_at || '')))
        .slice(0, 50));
    }
    if (event.type === 'notification.read_changed') {
      const ids = new Set(event.payload?.ids || []);
      if (!ids.size) return;
      if (snapshotLoading.current) ids.forEach((id) => pendingReads.current.set(id, event.payload?.readAt ?? null));
      setNotifications((items) => items.map((item) => (
        ids.has(item.id) ? {...item, read_at: event.payload?.readAt ?? null} : item
      )));
    }
  }, [refreshAccount, workspaceId]);

  useEffect(() => {
    pendingCreated.current.clear();
    pendingReads.current.clear();
    if (!workspaceId || !userId) {
      load();
      return () => { loadSequence.current += 1; };
    }
    let active = true;
    const subscription = subscribeWorkspaceEvents({
      workspaceId,
      userId,
      onEvent: handleRealtimeEvent,
      onReconnect: load,
    });
    subscription.ready.then(() => {
      if (active) load();
    });
    return () => {
      active = false;
      loadSequence.current += 1;
      snapshotLoading.current = false;
      subscription.unsubscribe();
    };
  }, [handleRealtimeEvent, load, userId, workspaceId]);

  const totalUnread = notifications.filter((notification) => !notification.read_at).length;

  const handleMarkAllAsRead = async () => {
    const readAt = new Date().toISOString();
    setNotifications((items) => items.map((item) => ({...item, read_at: item.read_at || readAt})));
    try {
      await markAllNotificationsRead(workspaceId);
    } catch (error) {
      load();
    }
  };

  const handleNotificationOpen = (notification) => {
    setOpen(null);
    if (notification.read_at) return;
    setNotifications((items) => items.map((item) => (
      item.id === notification.id ? {...item, read_at: new Date().toISOString()} : item
    )));
    markNotificationRead(notification.id).catch(load);
  };

  return (
    <>
      <IconButtonAnimate
        aria-label="Abrir notificações"
        aria-haspopup="menu"
        aria-expanded={Boolean(open)}
        data-tut="notifications"
        color={open ? 'primary' : 'default'}
        onClick={(event) => setOpen(event.currentTarget)}
        sx={{width: 40, height: 40}}
      >
        <Badge badgeContent={totalUnread} color="error">
          <Iconify icon="eva:bell-fill" width={20} height={20}/>
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={() => setOpen(null)}
        sx={{width: 380, p: 0, mt: 1.5, ml: 0.75}}
      >
        <Box sx={{display: 'flex', alignItems: 'center', py: 2, px: 2.5}}>
          <Box sx={{flexGrow: 1}}>
            <Typography variant="subtitle1">Notificações</Typography>
            <Typography variant="body2" sx={{color: 'text.secondary'}}>
              {totalUnread} {totalUnread === 1 ? 'não lida' : 'não lidas'}
            </Typography>
          </Box>

          {totalUnread > 0 && (
            <Tooltip title="Marcar todas como lidas">
              <IconButtonAnimate
                aria-label="Marcar todas como lidas"
                color="primary"
                onClick={handleMarkAllAsRead}
              >
                <Iconify icon="eva:done-all-fill" width={20} height={20}/>
              </IconButtonAnimate>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{borderStyle: 'dashed'}}/>

        <Scrollbar sx={{maxHeight: 440}}>
          {notifications.length ? (
            <List disablePadding>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onOpen={() => handleNotificationOpen(notification)}
                />
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center" sx={{py: 5, px: 2}}>
              Nenhuma notificação por enquanto.
            </Typography>
          )}
        </Scrollbar>
      </MenuPopover>
    </>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    created_at: PropTypes.string.isRequired,
    description: PropTypes.string,
    id: PropTypes.string.isRequired,
    path: PropTypes.string,
    read_at: PropTypes.string,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
};

function NotificationItem({notification, onOpen}) {
  const linkProps = notification.path ? {component: RouterLink, to: notification.path} : {};
  return (
    <ListItemButton
      {...linkProps}
      onClick={onOpen}
      sx={{py: 1.5, px: 2.5, mt: '1px', ...(!notification.read_at && {bgcolor: 'action.selected'})}}
    >
      <ListItemAvatar>
        <Avatar sx={{bgcolor: 'background.neutral', color: 'primary.main'}}>
          <Iconify icon={ICONS[notification.type] || 'eva:bell-fill'} width={22} height={22}/>
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={<Typography variant="subtitle2">{notification.title}</Typography>}
        secondary={
          <>
            <Typography component="span" variant="body2" sx={{display: 'block', color: 'text.secondary'}}>
              {notification.description}
            </Typography>
            <Typography component="span" variant="caption" sx={{mt: 0.5, display: 'flex', alignItems: 'center', color: 'text.disabled'}}>
              <Iconify icon="eva:clock-outline" sx={{mr: 0.5, width: 16, height: 16}}/>
              {fToNow(notification.created_at)}
            </Typography>
          </>
        }
      />
    </ListItemButton>
  );
}
